<template>
  <v-card
    flat
    class="px-5"
    :disabled="loading"
  >
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
    ></v-progress-linear>
    <v-row
      dense
      class="session-wrapper"
    >
      <v-col
        cols="4"
        class="session-info-wrapper"
      >
        <v-card
          flat
          color="var(--app-light-secondary-color)"
        >
          <div
            v-if="sessionAppointment"
            class="mr-4 mb-7"
          >
            <span class="session-day"> {{ sessionAppointment }}</span>
          </div>

          <div class="mr-4 text-subtitle-2">
            <div>{{ getDayArabicName(session.sessionDate) }}</div>
          </div>
          <div class="month-arabic-name mr-4">
            {{ getDayMonthHijri(session.sessionDate) }}
          </div>
          <div class="time-part mr-4 mb-2">
            <div>{{ getTimePartArabic(session.sessionDate) }}</div>
          </div>
        </v-card>
      </v-col>

      <v-col
        class="session-details pt-0"
        cols="8"
      >
        <v-card flat>
          <div class="mt-4 mr-4 text-subtitle-2">
            <div> {{ session.caseCode }}</div>
          </div>

          <v-card-title>
            {{ session.sessionType }}
          </v-card-title>

          <div class="mr-4 text-subtitle-2">
            <div>{{ session.court }}</div>
          </div>

          <div class="mt-4 mr-4 pb-2 text-subtitle-1">
            <v-row>
              <v-col>
                <template v-if="session.isVideoConference">
                  <div>
                    <template>
                      <span class="text-c-lite mr-1 px-2">
                        {{ local.home.upcomingSessions.remoteSession }}
                      </span>
                    </template>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          :title="local.home.upcomingSessions.copyLink"
                      color="var(--app-primary-color)"
                          class="copy-icon ml-1"
                          v-bind="attrs"
                          left
                          v-on="on"
                          @click="copyURL"
                        >
                          mdi-content-copy
                        </v-icon>
                      </template>
                      <span> {{ local.home.upcomingSessions.copyLink }}</span>
                    </v-tooltip>
                    <naj-button
                      v-if="isAllowedShowAttendanceRegister(session) && !session.userHasSessionAttendanceRecord && !showEnterSessionBtn"
                      type="button"
                      size="small"
                      class="float-left"
                      @click="confirmAttendanceRegister(session)"
                    >
                      {{ local.home.upcomingSessions.attendanceRegister }}
                    </naj-button>

                    <naj-button
                      v-if="(isAllowedShowAttendanceRegister(session) && session.userHasSessionAttendanceRecord && session.isVideoConference)
                        || showEnterSessionBtn"
                      size="small"
                      type="button"
                      class="float-left"
                      @click="openVideoConference(session.objectKey)"
                    >
                      {{ local.home.upcomingSessions.enterSession }}
                    </naj-button>
                  </div>
                </template>
                <template v-else>
                  <div>
                    {{ local.home.upcomingSessions.attendanceSession }}
                    <v-icon
                  color="var(--app-primary-color)"
                      class="copy-icon ml-1"
                      left
                    >
                      mdi-map-marker
                    </v-icon>
                  </div>
                </template>
              </v-col>
            </v-row>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <naj-confirmation
      :show-modal="showAttendanceRegister"
      :max-width="771"
      :title="local.home.upcomingSessions.attendanceRegister"
      :show-close-btn="true"
      @cancel="() => {
        showAttendanceRegister = false
        pledged = false;
      }"
    >
      <template slot="message-content">
        <v-row>
          <v-col>
            <div>
              <p class="attendanceRegister-title-header">
                {{ local.home.upcomingSessions.attendanceRegisterPart1 }}
              </p>
              <h6 class="attendanceRegister-title-condition">
                {{ local.home.upcomingSessions.termsAndConditions }}
              </h6>
            </div>
            <div class="details__documentList">
              <div
                class="details__documentItem"
                style="width:100%"
              >
                {{ local.home.upcomingSessions.attendanceRegisterFirstConditions }}
              </div>
              <div
                class="details__documentItem"
                style="width:100%"
              >
                {{ local.home.upcomingSessions.attendanceRegisterSecondConditions }}
              </div>
            </div>
            <p class="text-condition">
              {{ local.home.upcomingSessions.attendanceRegisterPart2 }}
            </p>
          </v-col>
        </v-row>
      </template>
      <template slot="actions">
        <naj-button
          v-if="selectedSession"
          :disabled="!pledged"
          @click="registerToSession(selectedSession.objectKey)"
        >
          {{ local.home.upcomingSessions.attendanceRegister }}
        </naj-button>
      </template>
      <template slot="footer-content">
        <naj-input
          :type-id="formFieldType.checkbox"
          :value.sync="pledged"
          :label="local.home.upcomingSessions.agreeOnTermsAndConditions"
        ></naj-input>
      </template>
    </naj-confirmation>

    <naj-confirmation
      :show-modal="showRedirectToSessionModal"
      :max-width="750"
      :is-persistent="true"
      :title="local.home.upcomingSessions.goToSession"
      :show-close-btn="false"
      @cancel="() => {
        showRedirectToSessionModal = false
        redirectToSessionLink = null;
      }"
    >
      <template slot="message-content">
        <v-row>
          <v-col>
            <naj-alert type="success">
              <template slot="content">
                {{ local.home.upcomingSessions.toRedirectToSessionLink }}
                <a
                  :href="redirectToSessionLink"
                  target="_blank"
                  class="redirect-session-link"
                >
                  {{ local.common.clickHere }}
                </a>
              </template>
            </naj-alert>
          </v-col>
        </v-row>
      </template>
    </naj-confirmation>
  </v-card>
</template>

<script>
import { getDayArabicName, getDayMonthHijri, getTimePartArabic, getDateDifference, toHijriDate, getTimePart, getTime12Format, isAm, getTimeSlot } from '@/helpers/utils';
import { createNamespacedHelpers } from 'vuex';
import { sessionTypes, caseDegrees, sessionStatus, userType } from '@/helpers/enum';
const { mapActions: mapCasesActions } = createNamespacedHelpers('CasesModule');
const { mapActions: mapGlobalActions } = createNamespacedHelpers('GlobalModule');

export default {
  components: {},
  props: {
    session: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      getDayArabicName,
      getDayMonthHijri,
      getTimePartArabic,
      getDateDifference,
      loading: false,
      showAttendanceRegisterValidation: false,
      attendanceRegisterValidationMessage: '',
      showAttendanceRegister: false,
      showRedirectToSessionModal: false,
      showEnterSessionBtn: false,
      sessionTypes,
      caseDegrees,
      sessionStatus,
      userType,
      userInfo: null,
      pledged: false,
      selectedSession: null,
      redirectToSessionLink: null,
    };
  },
  computed: {
    sessionAppointment() {
      const dateDifference = getDateDifference(this.session.sessionDate);
      if (dateDifference == 0) {
        return this.local.home.upcomingSessions.today;
      }
      if (dateDifference == 1) {
        return this.local.home.upcomingSessions.tomorrow;
      }
      return '';
    },

  },
  created() {
    this.getUserInfo()
      .then((user) => {
        this.userInfo = user;
      });
  },
  mounted() {
  },
  methods: {
    ...mapGlobalActions(['getUserInfo']),
    ...mapCasesActions([
      'getVideoConference',
      'validationAttendanceRegister',
      'addAttendanceRegisterToSession',

    ]),
    toHijriDate,
    isAm,
    getTimeSlot,
    getTime12Format,
    getTimePart,

    openVideoConferenceUrl() {
      this.loading = true;
      this.getVideoConference(this.session.objectKey)
        .then((res) => {
          window.open(res.data, '_blank');
        })
        .finally(() => (this.loading = false));
    },
    async copyURL() {
      if (this.session.videoConferenceUrl) {
        await navigator.clipboard.writeText(this.session.videoConferenceUrl);
        this.toaster.success(this.local.home.upcomingSessions.copied);
      }
    },
    isAllowedShowAttendanceRegister(session) {
      return (this.userInfo && this.userInfo.userType != userType.najizOrganization &&

        ((session.sessionStatusId == sessionStatus.new || session.sessionStatusId == sessionStatus.inProgress)

          && (session.sessionTypeId == sessionTypes.pleadingSession || session.sessionTypeId == sessionTypes.judgmentHearingSession

            || session.sessionTypeId == sessionTypes.firstHearingSession)));
    },
    confirmAttendanceRegister(session) {
      this.loading = true;
      this.selectedSession = session;

      this.validationAttendanceRegister(session.objectKey).then(() => {
        this.showAttendanceRegister = true;
      }).catch(() => {

      }).finally(() => (this.loading = false));
    },
    registerToSession(sessionObjectKey) {

      this.loading = true;
      this.addAttendanceRegisterToSession(sessionObjectKey).then(() => {
        this.showAttendanceRegister = false;
        this.pledged = false;

        this.toaster.success(this.local.home.upcomingSessions.registrationSuccessful);
        if (this.selectedSession && this.selectedSession.videoConferenceUrl) {
          this.openVideoConference(sessionObjectKey);
        }
      }).catch(() => {
        this.showAttendanceRegister = false;
      }).finally(() => (this.loading = false));
    },
    openVideoConference(sessionObjectKey) {
      this.loading = true;
      this.getVideoConference(sessionObjectKey)
        .then((res) => {
          this.redirectToSessionLink = res.data;
          this.showEnterSessionBtn = true;
          this.showRedirectToSessionModal = true;
        })
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style lang="scss" scoped>
    .session-wrapper {
        border: thin solid rgba(0, 0, 0, 0.12);

        .session-info-wrapper {
            display: flex;
            //justify-content: center;
            align-items: flex-end;
            background-color: var(--app-light-secondary-color);

            .session-day {
                font-size: 14px;
                font-weight: bold;
                background-color: var(--app-primary-color);
                color: #fff;
                padding: 4px 8px;
                border-radius: 3px;
            }

            .month-arabic-name {
                color: var(--app-dark-primary-color);
                font-size: 18px;
                font-weight: bold;
            }

            .time-part {
                color: var(--app-dark-primary-color);
                font-size: 16px;
                font-weight: bold;
            }
        }

        .session-details {
            .session-type {
                color: #a6b8bb;
            }
        }

        .copy-icon {
            cursor: pointer;
            font-size: 20px;
        }

        .videoConferenceUrl {
            color: var(--app-primary-color);
            cursor: pointer;
        }
    }

::v-deep .v-card__title {
  font-weight: bold;
  padding-bottom: 5px;
  padding-top: 5px;
  font-size: 18px;
}

.text-subtitle-2 {
  font-size: 16px !important;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
